/**
 * 🚢 수출입건 고르기 — 수출입관련 지출결의서 (v2.230.0 · 2026-10-06 · 개발 요청 #14)
 *
 * 사용자: 「지금은 수기로 (AS-26-004) 같은 오더번호를 적고 있는데 … 기존 수출입 목록에서 검색, 선택하고」.
 *   · 여러 건을 고를 수 있다(문서 하나에 여러 건 · 사용자 결정 10-06).
 *   · 찾기 = 오더번호(26-016 · AS-26-004)·품목·규격·엔드유저·영업번호 · 빈 칸이면 최근 30건.
 *   · ⭐금액은 보이지 않는다 — 작성자는 전 직원이고, 수출입 메뉴(단가·환율)는 경영관리 이상 전용이다.
 * 고른 것은 최종승인 때 그 수출입건으로 비용이 바로 배분된다(서버 expense-link.ts).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from '../lib/expense-api';

interface Props {
  value: api.ImportOrderTag[];
  onChange: (next: api.ImportOrderTag[]) => void;
}

export function ImportOrderPicker({ value, onChange }: Props) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['expense', 'import-orders', query],
    queryFn: () => api.searchImportOrders(query),
    enabled: open,
    staleTime: 30_000,
  });
  const picked = new Set(value.map((v) => v.importOrderId));
  const rows = (q.data ?? []).filter((r) => !picked.has(r.importOrderId));

  return (
    <div className="border border-violet-200 bg-violet-50/50 rounded p-2.5 space-y-2">
      <div className="text-xs font-semibold text-violet-900">
        🚢 수출입건 * <span className="font-normal text-slate-500">— 이 비용이 걸린 수입 오더 (여러 건 가능 · 최종승인 때 그 건들로 비용이 배분됩니다)</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 && <span className="text-xs text-rose-600">아직 고르지 않았습니다</span>}
        {value.map((v) => (
          <span key={`${v.importOrderId}-${v.refNo}`} className="inline-flex items-center gap-1 bg-white border border-violet-300 rounded px-2 py-0.5 text-xs">
            <b className="text-violet-800">{v.refNo}</b>
            <span className="text-slate-600 max-w-[22rem] truncate" title={v.summary}>{v.summary}</span>
            <button type="button" aria-label={`${v.refNo} 빼기`} className="text-slate-400 hover:text-rose-600 px-0.5"
              onClick={() => onChange(value.filter((x) => x !== v))}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          className="ui-input ui-input-sm flex-1"
          value={text}
          placeholder="오더번호(26-016 · AS-26-004)·품목·엔드유저·영업번호 — Enter 로 찾기"
          onFocus={() => setOpen(true)}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); setOpen(true); setQuery(text.trim()); }
          }}
        />
        <button type="button" className="btn btn-secondary btn-sm"
          onClick={() => { setOpen(true); setQuery(text.trim()); }}>찾기</button>
        {open && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>닫기</button>
        )}
      </div>
      {open && (
        <div className="max-h-56 overflow-y-auto bg-white border border-slate-200 rounded">
          {q.isLoading && <div className="px-3 py-2 text-xs text-slate-500">찾는 중…</div>}
          {q.isError && <div className="px-3 py-2 text-xs text-rose-600">{(q.error as Error).message}</div>}
          {!q.isLoading && !q.isError && rows.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">{query ? `「${query}」 로 찾은 수출입건이 없습니다` : '수출입건이 없습니다'}</div>
          )}
          {rows.map((r) => (
            <button
              key={r.importOrderId}
              type="button"
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 border-b border-slate-100 last:border-b-0 flex gap-2 items-baseline"
              onClick={() => onChange([...value, { importOrderId: r.importOrderId, refNo: r.refNo, summary: r.summary }])}
            >
              <b className="text-violet-800 w-[5.5rem] shrink-0">{r.refNo}</b>
              {r.isRma && <span className="text-[11px] px-1 rounded bg-amber-100 text-amber-800 shrink-0">RMA</span>}
              <span className="text-slate-400 shrink-0 w-[5.5rem]">{r.orderDate ?? ''}</span>
              <span className="text-slate-700 truncate">{r.summary}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
